import Image from 'app/Components/Shared/Image/image';
import React, {useState, useEffect, useRef} from 'react';
import BtnSubmit from 'app/Components/Shared/Buttons/BtnSubmit';
import ReactSelect from 'app/Components/Shared/ReactSelect/ReactSelect';
import CheckBox from 'app/Components/Shared/CheckBox/Checkbox';
import TextInput from 'app/Components/Shared/TextInput/TextInput';
import BtnBlackBorder from 'app/Components/Shared/Buttons/BtnWhiteBlackBorderSmallSize';
import BtnWhiteBlackBorder from 'app/Components/Shared/Buttons/BtnWhiteBlackBorderSmallSize';
import DatePicker from 'app/Components/Shared/DatePicker/DatePicker';
import InputWithActions from 'app/Components/Shared/InputWithActions/InputWithActions';
import SelectWithCheckboxes from 'app/Components/Shared/SelectWithCheckboxes/SelectWithCheckboxes';
import FilterChips from './FilterChips';
import {parse} from 'date-fns';
import {isMobile} from 'react-device-detect';

import './FilterBar.scss';

const FilterBar = ({currentFilterValues , setCurrentPage, tableFilters, submitSearching, onClearFilters, onFilterChange, chips, onRemoveChip }) => {
  const filters = tableFilters.fields;
  const [filterValues, setFilterValues] = useState({});
  const [validationError, setValidationError] = useState('');
  const [mobileFilterValues, setMobileFilterValues] = useState({});
  const [showMobileFilterPopup, setShowMobileFilterPopup] = useState(false);
  const startDate = useRef(null);
  const endDate = useRef(null);

  const datePickerInputWidth = isMobile ? 160 : 140;


  // Initialize default values when component mounts
  useEffect(() => {
    setDefaultFilterValues();
  }, []);

  useEffect(() => {
    if (currentFilterValues) {
      setFilterValues(currentFilterValues);
    }
  }, [currentFilterValues]);

  useEffect(() => {
    if (showMobileFilterPopup) {
      setMobileFilterValues(filterValues);
    }
  }, [showMobileFilterPopup]);

  function setDefaultFilterValues() {
    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        if (filter.defaultValue && typeof filter.defaultValue === 'object' && Object.keys(filter.defaultValue).length > 0) {
          Object.entries(filter.defaultValue).forEach(([key, value]) => {
            setFilterValues((prevFilterValues) => ({
              ...prevFilterValues,
              [key]: value,
            }));
          });
        }
      });
    }
  }

  const handleFilterChange = (filter, value) => {

    setFilterValues((prevFilterValues) => {
      const newValues = {
        ...prevFilterValues,
        [filter]: value,
      };

      if (onFilterChange) {
        onFilterChange(filter, value, newValues);
      }

      return newValues;
    });

    if (validationError) {
      setValidationError('');
    }
  };

  const validateDateRange = () => {
    const hasStartDate = filterValues.startDate && filterValues.startDate.trim() !== '';
    const hasEndDate = filterValues.endDate && filterValues.endDate.trim() !== '';


    if (hasStartDate && hasEndDate) {
      const toDate = (val) => {
        if (val instanceof Date) return val;
        try {
          return parse(val, 'dd/MM/yyyy', new Date());
        } catch {
          return null;
        }
      };

      const startDate = toDate(filterValues.startDate);
      const endDate = toDate(filterValues.endDate);

      if (startDate && endDate && startDate > endDate) {
        return 'תאריך ההתחלה חייב להיות לפני תאריך הסיום';
      }
    }

    return null; // No validation errors
  };

  const dateRangeChange = (key, date) => {
    const toDate = (val) => {
      if (val instanceof Date) return val;
      try {
        return parse(val, 'dd/MM/yyyy', new Date());
      } catch {
        return null;
      }
    };
    if (
      (key === 'startDate' && filterValues.endDate && toDate(date) > toDate(filterValues.endDate)) ||
      (key === 'endDate' && filterValues.startDate && toDate(date) < toDate(filterValues.startDate))
    ) {
      return;
    } else {
      handleFilterChange(key, date);
    }
  };

  const handleSearching = () => {

    setCurrentPage(1);
    if (submitSearching) {
      setValidationError('');
      const error = validateDateRange();
      if (error) {
        setValidationError(error);
        return;
      }
      submitSearching(filterValues);
    }
  };

  const handleClearFilters = () => {
    setFilterValues({});
    setDefaultFilterValues();

    if (onClearFilters) {
      onClearFilters();
    }
  };

  function getSelectBoxValue(filter) {
    if (filter.value && !filterValues[filter.key]) {
      setFilterValues((prevFilterValues) => ({
        ...prevFilterValues,
        [filter.key]: filter.value,
      }));
      return filter.options.filter((option) => option.key == filterValues[filter.value]);
    }
    return filter.options.filter((option) => option.key == filterValues[filter.key]);
  }

  const renderFilterField = (filter) => {
    const value = filterValues[filter.key] || filter.defaultValue || '';

    switch (filter.type) {
      case 'text':
        return (
          <TextInput
            value={value}
            id='FilterBarTextInput'
            placeholder={filter.placeholder}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
          />
        );
 

      case 'select':
        return (
          <ReactSelect
            getOptionLabel={(option) => option.value}
            getOptionValue={(option) => option.key}
            placeholder={filter.placeholder}
            value={getSelectBoxValue(filter)}
            name='filter-bar-select-box'
            options={filter.options}
            onChange={(e) => handleFilterChange(filter.key, e.key)}
          />
        );
      case 'selectWithCheckboxes':
        return (
            <SelectWithCheckboxes
                options={filter.options}
                onSelectionChange={(selectedKeys) => handleFilterChange(filter.key, selectedKeys)}
                name={filter.key}
                placeholder={filter.placeholder}
                selectedValues={filterValues[filter.key] || []}
          />
        );

      case 'date':
        return (
          <label className='has-float-label-required'>
            <DatePicker
              selectedDate={filterValues.startDate}
              setSelectedDate={(date) => handleFilterChange(filter, date)}
              numOfYearsBack={filter.numOfYearsBack}
              inputWidth={datePickerInputWidth}
            />
            <span className='float-label'>בחר תאריך</span>
          </label>
        );

      case 'dateRange':
        return (
          <div className='date-range-container'>
            <div className='date-range-input'>
                <DatePicker
                  maxDate={filterValues.endDate || filter.maxDate}
                  dependedDate={filter.minDate || ''}
                  numOfYearsBack={filter.numOfYearsBack}
                  selectedDate={filterValues.startDate || filter.defaultValue?.startDate}
                  setSelectedDate={(date) => dateRangeChange('startDate', date)}
                  title={'תאריך התחלה'}
                  placeholder={'מתאריך'}
                  inputWidth={datePickerInputWidth}
                />

            </div>
            <div className='date-range-input'>
                <DatePicker
                  maxDate={filter.maxDate}
                  dependedDate={filterValues.startDate || filter.minDate}
                  numOfYearsBack={filter.numOfYearsBack}
                  selectedDate={filterValues.endDate || filter.defaultValue?.endDate}
                  setSelectedDate={(date) => dateRangeChange('endDate', date)}
                  title={'תאריך סיום'}
                  placeholder={'לתאריך'}
                  inputWidth={datePickerInputWidth}
                />

            </div>
          </div>
        );

      case 'custom':
        return filter.render ? filter.render(filterValues) : null;

      default:
        return null;
    }
  };

  if (!filters || filters.length === 0) {
    return null;
  }


  const getWrapperClasses = () => {
    const classes = ['filter-bar-content-wrapper'];

    if (tableFilters?.showClearButton) classes.push('has-clear-button');
    if (submitSearching) classes.push('has-action-button');
    return classes.join(' ');
  };

  const renderChips = () => {
    if (!chips || chips.length === 0) return null;
    return (
      <FilterChips chips={chips} onRemoveChip={onRemoveChip} />
    );
  };

  const getDesktopFilterBar = () => {
    return (
      <>
          <div className={getWrapperClasses()}>
            <div className='filter-bar-content content-left'>
              <Image src={require('./Icon/filter.svg')} />
              בחר מסננים:
            </div>
            <div className='filter-bar-content content-right'>
              {filters.map((filter) => (
                <div key={filter.key} className={`filter-field`} style={{width: filter.width}}>
                  {filter.label && <label className='filter-label'>{filter.label}</label>}
                  <div className='filter-input'>{renderFilterField(filter)}</div>
                </div>
              ))}
            </div>
            {tableFilters?.showClearButton && (
              <div className='filter-bar-content content-clear'>
                <div className='filter-actions'>
                  <a className='link' onClick={handleClearFilters}>
                    איפוס
                  </a>
                </div>
              </div>
            )}
            {submitSearching && (
              <div className='filter-bar-content content-action'>
                <div className='filter-action-button'>
                  <BtnWhiteBlackBorder onclick={() => handleSearching()} text={'חיפוש'} />
                  {validationError && (
                    <div className='validation-error' style={{color: 'red', fontSize: '12px', marginTop: '4px'}}>
                      {validationError}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        {renderChips()}
        </>
    );
  };



  const getMobileFilterBar = () => {
    return ( 
      <>
      { !showMobileFilterPopup && (
        <div className='mobile-filter-bar'>
            <button className='mobile-filter-search-btn' onClick={() => setShowMobileFilterPopup(true)}>
                מסננים  <Image src={require('./Icon/filter.svg')} /> 
            </button>
        </div>  
      )}
    </>
    );
  };
  const renderMobileFilterPopup = () => {
    if (!showMobileFilterPopup || !filters) return null;
    return (
      <div className='mobile-filter-popup-content'>
         <div className='mobile-filter-popup-header'>
          <Image src={require('./Icon/removeIcon.svg')} onClick={() => setShowMobileFilterPopup(false)} />
          <span> סינון לפי </span>
         </div>
        <div className='popup-content'>
            <div className='filter-bar-content'>
              {filters.map((filter) => (
                <div key={filter.key} className={`filter-field`} style={{width: filter.width}}>
                  {filter.label && <label className='filter-label'>{filter.label}</label>}
                  <div className='filter-input'>{renderFilterField(filter)}</div>
                </div>
              ))}
              {renderChips()}
            </div>
            <div className='filter-action-button'>
                <button onClick={() => onClearFilters()}>נקה בחירה</button>
                <button onClick={() => {submitSearching(filterValues) ; setShowMobileFilterPopup(false)}}>הצג תוצאות</button>
            </div>
          </div>
        </div>

        
    );
  };


  return (
    <div className='generic-filter-bar'>
      <>{ isMobile ? getMobileFilterBar() : getDesktopFilterBar()}</>
      <>{renderMobileFilterPopup()}</>
    </div>
  );
};

export default FilterBar;
